/*
 *  @providesModule FTUEController
 */
import ClientPropertyConstants, {FTUECreateProfilePosition} from 'ClientPropertyConstants';
import FTUEConstants from 'FTUEConstants';
import {ORDERED_CONVERSIONS_STEPS} from 'FTUEConversionsTypes';
import GlobalEventEmitter from 'GlobalEventEmitter';
import PlayableMatchesStore from 'PlayableMatchesStore';
import Prefs from 'Preferences';
import SyncFTUEController from 'SyncFTUEController';
import _ from 'lodash';
import {Platform} from 'react-native';

class FTUEController {
  skipFTUE() {
    const resetableTutorials = FTUEConstants || {};

    if (resetableTutorials && Object.keys(resetableTutorials)) {
      _.forEach(_.values(resetableTutorials), (value: string) => {
        Prefs.set({[value]: true});
      });
    }

    Prefs.set({seenPendingTutorialSKZ: true});
    this.setHasSeenLegacyTutorial(true);
  }

  resetAllTutorials(isLogout: boolean) {
    let resetableTutorials = {};
    if (isLogout) {
      resetableTutorials = FTUEConstants;

      if (resetableTutorials && Object.keys(resetableTutorials)) {
        const keys = Object.keys(resetableTutorials);
        _.forEach(keys, (key: string) => {
          Prefs.set({[resetableTutorials[key]]: false});
        });
      }

      Prefs.set({seenPendingTutorialSKZ: false});

      this.setHasSeenLegacyTutorial(false);
    }
  }
  // This function maps to the legacy FTUE Tab Tutorial.
  // All other FTUE steps will be check against this key as well,
  // ensuring that legacy users do not see the new FTUE Flow.
  hasSeenLegacyTutorial(): boolean {
    if (this.isTutorialTrainerActive()) {
      return false;
    }

    const tabValue = Prefs.get(FTUEConstants.kFTUELegacyTabTutorialKey);
    if (tabValue == 'undefined' || tabValue == null) {
      return false;
    }
    if (Platform.OS == 'ios') {
      return !tabValue; // Values reversed due to original iOS idiom
    } else {
      return tabValue;
    }
  }

  hasCompletedFTUE(): boolean {
    return (
      this.hasCompletedAsyncFTUE() ||
      SyncFTUEController.isSyncFTUEComplete() ||
      this.hasSeenLegacyTutorial()
    );
  }

  hasCompletedAsyncFTUE(): boolean {
    return this.hasSeenMatchStreamTutorial() || this.hasSeenLegacyTutorial();
  }

  setHasSeenLegacyTutorial(value: boolean) {
    if (Platform.OS == 'ios') {
      Prefs.set({[FTUEConstants.kFTUELegacyTabTutorialKey]: !value}); // Values reversed due to original iOS idiom
    } else {
      Prefs.set({[FTUEConstants.kFTUELegacyTabTutorialKey]: value});
    }
  }

  hasSeenSlideshow(): boolean {
    const vsValue = Prefs.get(FTUEConstants.kSeenFTUESlideshowKey);
    return vsValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenSlideshow(value: boolean) {
    Prefs.set({[FTUEConstants.kSeenFTUESlideshowKey]: value});
  }

  setHasCreatedProfile(value: boolean) {
    Prefs.set({[FTUEConstants.kCreatedProfileTutorialKey]: value});
  }

  hasCreatedProfile(): boolean {
    const vsValue = Prefs.get(FTUEConstants.kCreatedProfileTutorialKey);
    return vsValue || this.hasSeenLegacyTutorial();
  }

  hasSeenTabTutorial(): boolean {
    const vsValue = Prefs.get(FTUEConstants.kTabTutorialKey);
    return vsValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenTabTutorial(value: boolean) {
    Prefs.set({[FTUEConstants.kTabTutorialKey]: value});
  }

  // This is set to true as soon as the versus screen makes the join call for the first match. For the
  // first FTUE match, this happens as soon as the screen is displayed.
  hasJoinedFirstFTUEMatch(): boolean {
    const vsValue = Prefs.get(FTUEConstants.kJoinedFirstFTUETournamentKey);
    return vsValue || this.hasSeenLegacyTutorial();
  }

  setHasJoinedFirstFTUEMatch(value: boolean) {
    Prefs.set({[FTUEConstants.kJoinedFirstFTUETournamentKey]: value});
  }

  // This is set to true when transitioning to the game after having seen the first versus screen
  hasSeenVersusTutorialOne(): boolean {
    const vsValue = Prefs.get(FTUEConstants.kVersusTutorialOneKey);
    return vsValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenVersusTutorialOne(value: boolean) {
    PlayableMatchesStore.clearMatchSelectCache();
    Prefs.set({[FTUEConstants.kVersusTutorialOneKey]: value});
  }

  hasSeenVersusTutorialTwo(): boolean {
    const vsValue = Prefs.get(FTUEConstants.kVersusTutorialTwoKey);
    return vsValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenVersusTutorialTwo(value: boolean) {
    PlayableMatchesStore.clearMatchSelectCache();
    Prefs.set({[FTUEConstants.kVersusTutorialTwoKey]: value});
  }

  setHasSeenVersusTutorials() {
    this.setHasSeenVersusTutorialOne(true);
    this.setHasSeenVersusTutorialTwo(true);
  }

  hasSeenMatchSelectTutorial(): boolean {
    const matchSelectTutorial = Prefs.get(FTUEConstants.kMatchSelectTutorialKey);
    return matchSelectTutorial || this.hasSeenLegacyTutorial();
  }

  setHasSeenMatchSelectTutorial(value: boolean) {
    Prefs.set({[FTUEConstants.kMatchSelectTutorialKey]: value});

    if (Platform.OS === 'android') {
      // Also mark the unused legacy tutorials as complete; Android checks these
      // flags to determine if the user is still in the FTUE sequence. If so,
      // it will remount the root React view every time the HomeActivity starts,
      // preventing users from resuming where they left off.
      //
      // See `resetReactRootView` and `isInFTUESequence` in SkillzReactBridge.java
      this.setLegacyFTUEComplete(value);
    }
  }

  hasSeenMatchStreamTutorial(): boolean {
    if (this.isTutorialTrainerActive()) {
      return false;
    }

    const matchStreamTutorial = Prefs.get(FTUEConstants.kMatchStreamTutorialKey);
    return matchStreamTutorial || this.hasSeenLegacyTutorial();
  }

  hasCompletedSecondMatch(): boolean {
    const hasCompletedSecondMatch = Prefs.get(FTUEConstants.kSecondMatchCompleted);
    return hasCompletedSecondMatch || this.hasSeenLegacyTutorial();
  }

  setHasCompletedSecondMatch(value: boolean) {
    Prefs.set({[FTUEConstants.kSecondMatchCompleted]: value});
  }

  setHasSeenMatchStreamTutorial(value: boolean) {
    GlobalEventEmitter.emit('finishedMatchStreamTutorial');
    Prefs.set({[FTUEConstants.kMatchStreamTutorialKey]: value});
  }

  hasSeenPLOWWinTutorialOne(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWWinTutorialOneKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWWinTutorialOne(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWWinTutorialOneKey]: value});
  }

  hasSeenPLOWWinTutorialTwo(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWWinTutorialTwoKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWWinTutorialTwo(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWWinTutorialTwoKey]: value});
  }

  hasSeenPLOWWinTutorialThree(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWWinTutorialThreeKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWWinTutorialThree(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWWinTutorialThreeKey]: value});
  }

  hasSeenPLOWCompletedTutorialOne(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWCompletedTutorialOneKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWCompletedTutorialOne(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWCompletedTutorialOneKey]: value});
  }

  hasSeenPLOWCompletedTutorialTwo(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWCompletedTutorialTwoKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWCompletedTutorialTwo(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWCompletedTutorialTwoKey]: value});
  }

  hasSeenPLOWPendLossTutorialOne(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWPendLossTutorialOneKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWPendLossTutorialOne(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWPendLossTutorialOneKey]: value});
  }

  hasSeenPLOWPendLossTutorialTwo(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWPendLossTutorialTwoKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWPendLossTutorialTwo(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWPendLossTutorialTwoKey]: value});
  }

  hasSeenPLOWPendLossTutorialThree(): boolean {
    const plowValue = Prefs.get(FTUEConstants.kPLOWPendLossTutorialThreeKey);
    return plowValue || this.hasSeenLegacyTutorial();
  }

  setHasSeenPLOWPendLossTutorialThree(value: boolean) {
    Prefs.set({[FTUEConstants.kPLOWPendLossTutorialThreeKey]: value});
  }

  setHasSeenAccountScreenBalanceSplit(value: boolean) {
    Prefs.set({[FTUEConstants.kAccountBalanceSplitKey]: value});
  }

  hasSeenAccountScreenBalanceSplit(): boolean {
    const value = Prefs.get(FTUEConstants.kAccountBalanceSplitKey);
    return value || false;
  }

  setHasSeenHomeScreenWalletTooltip(value: boolean) {
    Prefs.set({[FTUEConstants.kHomeWalletTooltipKey]: value});
  }

  hasSeenHomeScreenWalletTooltip(): boolean {
    // Disable FTUE for web users
    if (Platform.OS === 'web') {
      return true;
    }
    const value = Prefs.get(FTUEConstants.kHomeWalletTooltipKey);
    return value || false;
  }

  setHasCompletedWelcomeFTUE(value: boolean) {
    if (Platform.OS == 'ios') {
      Prefs.set({[FTUEConstants.kShouldWalkthroughVersus]: !value}); // Values reversed due to original iOS idiom
    } else {
      Prefs.set({[FTUEConstants.kShouldWalkthroughVersus]: value});
      Prefs.set({kLegacyFirstLaunchKey: !value});
    }
  }

  hasCompletedWelcomeFTUE(): boolean {
    let welcomeValue = false;
    if (Platform.OS == 'ios') {
      welcomeValue = !Prefs.get(FTUEConstants.kShouldWalkthroughVersus); // Values reversed due to original iOS idiom
    } else {
      welcomeValue = Prefs.get(FTUEConstants.kShouldWalkthroughVersus);
    }
    return welcomeValue || this.hasSeenLegacyTutorial();
  }

  hasSeenLiveBracketTutorial(): boolean {
    const liveBracketTutorial = Prefs.get(FTUEConstants.kLiveBracketTutorial);
    return liveBracketTutorial;
  }

  setHasSeenLiveBracketTutorial(value: boolean) {
    Prefs.set({[FTUEConstants.kLiveBracketTutorial]: value});
  }

  shouldDisplaySlideshowOnLaunch(): boolean {
    if (this.hasJoinedFirstFTUEMatch() || this.hasCompletedFTUE()) {
      return false;
    }

    // legacy slideshow should only display once, OTA FTUESlideshow should display as long as we
    // haven't joined the first match yet
    if (!ClientPropertyConstants.getFTUESlideshowV2Enabled()) {
      return !this.hasSeenSlideshow();
    }

    return true;
  }

  isNeedsDisplayNativeSlideshow(): boolean {
    return !ClientPropertyConstants.getFTUESlideshowV2Enabled() && !this.hasSeenSlideshow();
  }

  shouldDisplayCreateProfileOnLaunch(isFirstLaunchForUser: boolean): boolean {
    if (this.hasCompletedAsyncFTUE() || this.hasCreatedProfile()) {
      return false;
    }

    if (SyncFTUEController.isSyncOrHybridFTUE()) {
      return isFirstLaunchForUser;
    }

    if (
      ClientPropertyConstants.getProfilePositionInFTUE() == FTUECreateProfilePosition.FirstOpen &&
      !this.hasSeenVersusTutorialOne()
    ) {
      return true;
    }

    if (
      ClientPropertyConstants.getProfilePositionInFTUE() ==
        FTUECreateProfilePosition.AfterFirstGame &&
      this.hasJoinedFirstFTUEMatch() &&
      !this.hasSeenVersusTutorialTwo()
    ) {
      return true;
    }

    if (
      ClientPropertyConstants.getProfilePositionInFTUE() ==
        FTUECreateProfilePosition.AfterSecondGame &&
      this.hasSeenVersusTutorialTwo()
    ) {
      return true;
    }

    return false;
  }

  shouldGoToVersusFromProfile(): boolean {
    /**
     * If we are skipping the Z Matches, we should not go to VS from profile screen.
     * This ensures that the route screen will be added to the route stack on Skillz SDK initialization,
     * which enables us to navigate from Profile to Home Screen after pressing 'Next' on the Profile screen.
     */
    if (ClientPropertyConstants.getFTUESkipMatchTutorial()) {
      return false;
    }

    if (this.hasCompletedAsyncFTUE() || SyncFTUEController.checkFTUEScreenEligibility()) {
      return true;
    }

    /* If we have Sync FTUE but no Sync Bot, we expect to go back to the Home Screen after creating our
    profile (skip the FTUE). Therefore, we don't want to go to VS from profile screen, so return false. */
    if (SyncFTUEController.shouldSkipFTUESyncBotMissing()) {
      return false;
    }

    return (
      ClientPropertyConstants.getProfilePositionInFTUE() == FTUECreateProfilePosition.FirstOpen
    );
  }

  shouldGoToCreateProfileFromResults(): boolean {
    if (
      this.hasCompletedAsyncFTUE() ||
      SyncFTUEController.checkFTUEScreenEligibility() ||
      this.hasCreatedProfile()
    ) {
      return false;
    }

    if (
      ClientPropertyConstants.getProfilePositionInFTUE() ==
        FTUECreateProfilePosition.AfterFirstGame &&
      this.hasSeenVersusTutorialOne() &&
      !this.hasSeenVersusTutorialTwo()
    ) {
      return true;
    }

    if (
      ClientPropertyConstants.getProfilePositionInFTUE() ==
        FTUECreateProfilePosition.AfterSecondGame &&
      this.hasSeenVersusTutorialTwo()
    ) {
      return true;
    }

    return false;
  }

  setLegacyFTUEComplete(value: boolean) {
    this.setHasSeenLegacyTutorial(value);
    this.setHasSeenMatchStreamTutorial(value);
  }

  private hasSeenTutorialTrainer(): boolean {
    const currentIndex = Prefs.get(FTUEConstants.conversionsStepIndex) ?? 0;
    const disabled = Prefs.get(FTUEConstants.conversionsDisabled) ?? false;
    return currentIndex > 0 && !disabled;
  }

  private hasCompletedTutorialTrainer(): boolean {
    const currentIndex = Prefs.get(FTUEConstants.conversionsStepIndex) ?? 0;
    return ORDERED_CONVERSIONS_STEPS[currentIndex] === 'completed';
  }

  /**
   * Returns whether the tutorial trainer flow is active.
   *
   * This is needed to override some of the other FTUE checks--see
   * hasSeenMatchStreamTutorial and hasSeenLegacyTutorial above.
   *
   * Ideally, we should just deprecate the older FTUE logic (match stream +
   * legacy) and any of their usages, but there are many areas in the app that
   * depend on this state, so we'll do the least breaking changes for now.
   *
   * This is necessary because we have an edge case where the native code
   * (Android specific) depends on some of this state.
   * See https://github.com/skillz/sdk-mobile/pull/6219
   */
  private isTutorialTrainerActive(): boolean {
    return (
      !ClientPropertyConstants.getFTUESkipMatchTutorial() &&
      ClientPropertyConstants.getIsAsyncFtueBotEnabled() &&
      this.hasSeenTutorialTrainer() &&
      !this.hasCompletedTutorialTrainer()
    );
  }

  setHasSeenLoyaltyStoreInfoTooltip(value: boolean) {
    Prefs.set({[FTUEConstants.kLoyaltyStoreInfoTooltipKey]: value});
  }

  hasSeenLoyaltyStoreInfoTooltip(): boolean {
    const value = Prefs.get(FTUEConstants.kLoyaltyStoreInfoTooltipKey);
    return value || false;
  }

  // Lobby FTUE Tooltips (Store, Menu, Add Cash)
  
  setHasSeenLobbyStoreTooltip(value: boolean) {
    Prefs.set({[FTUEConstants.kLobbyStoreTooltipKey]: value});
  }

  hasSeenLobbyStoreTooltip(): boolean {
    const value = Prefs.get(FTUEConstants.kLobbyStoreTooltipKey);
    return value || false;
  }

  setHasSeenLobbyMenuTooltip(value: boolean) {
    Prefs.set({[FTUEConstants.kLobbyMenuTooltipKey]: value});
  }

  hasSeenLobbyMenuTooltip(): boolean {
    const value = Prefs.get(FTUEConstants.kLobbyMenuTooltipKey);
    return value || false;
  }

  setHasSeenLobbyAddCashTooltip(value: boolean) {
    Prefs.set({[FTUEConstants.kLobbyAddCashTooltipKey]: value});
  }

  hasSeenLobbyAddCashTooltip(): boolean {
    const value = Prefs.get(FTUEConstants.kLobbyAddCashTooltipKey);
    return value || false;
  }

  // User Path Selection (Beginner/Pro) Storage
  // Store user's selection from WelcomeBonusScreen
  // This persists across sessions until save account is completed
  
  /**
   * Set the user's FTUE path selection ('BEGINNER' or 'PRO')
   * @param pathSelection - The user's selected path
   */
  setUserPathSelection(pathSelection: 'BEGINNER' | 'PRO') {
    Prefs.set({kUserPathSelection: pathSelection});
  }

  /**
   * Get the user's FTUE path selection ('BEGINNER' or 'PRO')
   * Returns null if no selection has been made
   */
  getUserPathSelection(): 'BEGINNER' | 'PRO' | null {
    const selection = Prefs.get('kUserPathSelection');
    return selection || null;
  }

  /**
   * Check if user has made a path selection
   */
  hasUserPathSelection(): boolean {
    return this.getUserPathSelection() !== null;
  }

  /**
   * Clear the user's path selection
   * Should be called when user completes save account flow
   */
  clearUserPathSelection() {
    Prefs.set({kUserPathSelection: null});
  }
}

const SharedInstance = new FTUEController();
export {SharedInstance as default};
